import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { userAPI } from "../services/api";
import { useAuth } from "../Context/AuthContext";
import { leadStageToRouteMap } from "../Utils/constants";

/**
 * SSO landing page for partner (GC / loan-aggregator) redirects.
 * The backend redirect sends the user here as: /sso?token=<USER_JWT>&next=partial
 *
 *  1. Store the pre-authenticated token (logs the user in).
 *  2. Fetch the user's geo-location (best-effort).
 *  3. Warm the session via the profile API.
 *  4. Route the user into the correct journey step.
 */

// Maps the `next` hint from the redirect URL to an in-app route.
const nextToRouteMap = {
  "company-info": "/apply/employment-status",
  partial: "/apply/partial-details",
  employment: "/apply/email",
  aa: "/apply/account-aggregator",
  "account-aggregator": "/apply/account-aggregator",
};

const fetchGeoLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude.toString(),
          lng: pos.coords.longitude.toString(),
        }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

export default function SsoLanding() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return; // guard against React 18 double-invoke
    hasRun.current = true;

    const token = searchParams.get("token");
    const next = searchParams.get("next");

    const run = async () => {
      if (!token) {
        setError("Missing SSO token");
        setTimeout(() => navigate("/", { replace: true }), 1500);
        return;
      }

      // 1. Store the pre-authenticated token.
      login(token);

      // 2. Fetch geo-location (best-effort) and stash for the partial-details page.
      try {
        const geo = await fetchGeoLocation();
        if (geo) {
          sessionStorage.setItem("gcGeoLocation", JSON.stringify(geo));
        }
      } catch {
        /* geolocation is best-effort */
      }

      // 3. Warm the session (best-effort).
      try {
        await userAPI.getProfile();
      } catch {
        /* non-blocking */
      }

      // 4. Route into the journey. Prefer the live lead stage; fall back to `next`.
      try {
        const journeyData = await userAPI.getJourney();
        const stage =
          journeyData?.journey?.lead_stage || journeyData?.lead_stage;
        const stageRoute = stage ? leadStageToRouteMap[stage] : null;
        const nextRoute = next ? nextToRouteMap[next] : null;
        navigate(stageRoute || nextRoute || "/apply/partial-details", {
          replace: true,
        });
      } catch {
        navigate(nextToRouteMap[next] || "/apply/partial-details", {
          replace: true,
        });
      }
    };

    run();
  }, [searchParams, login, navigate]);

  return (
    <section className="w-full min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9]">
      <div className="flex flex-col items-center gap-4">
        {error ? (
          <p className="text-red-500 text-sm font-medium">{error}</p>
        ) : (
          <>
            <svg
              className="animate-spin h-10 w-10 text-[#FF6B35]"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-[#64748B] text-sm font-medium">
              Signing you in securely...
            </p>
          </>
        )}
      </div>
    </section>
  );
}
